import { createSlice } from '@reduxjs/toolkit';
import { ENUM_LOCALSTORAGE } from '../../enums/enums';
import { getLcs, setLcs } from 'src/utils';

const fillGroupHash = (state, data) => {
  const newHash = { ...state.group.hash };

  data.forEach((el) => {
    const oldGroupInfo = newHash[el.groupId] || {};
    const parentId = oldGroupInfo?.parentId || oldGroupInfo?.parentId;
    newHash[el.groupId] = { ...oldGroupInfo, ...el };
    if (parentId) newHash[el.groupId].parentId = parentId;
  });

  state.group.hash = newHash;
};

const addGroupHash = (state, id, data) => {
  const parentId = data?.parentId || state.group.hash[id]?.parentId;
  state.group.hash[id] = data;

  if (parentId) state.group.hash[id].parentId = parentId;
};

const updateChildrenItem = (state, id, newParentId, data) => {
  let parentId = newParentId;
  if (!parentId) {
    parentId = state.group.hash[id]?.parentId;
    if (!parentId) return;
  }

  const groupIndx = state.group.children[parentId]?.findIndex((el) => el.groupId == id) || -1;
  if (groupIndx < 0) return;

  let newData = {};
  const currentGroupInfo = state.group.children[parentId]?.[groupIndx];
  if (currentGroupInfo) newData = { ...currentGroupInfo };

  state.group.children[parentId][groupIndx] = { ...newData, ...data };
};

const userSlice = createSlice({
  name: 'user',
  initialState: {
    id: getLcs(ENUM_LOCALSTORAGE.userId),
    login: getLcs(ENUM_LOCALSTORAGE.login),
    type: getLcs(ENUM_LOCALSTORAGE.type),
    superUser: getLcs(ENUM_LOCALSTORAGE.superUser),
    group: {
      main: {},
      children: {},
      hash: {}
    },
    // @type {[groupId] : bonusInfo }
    bonus: {},
    // @type {[groupId] : promocodeInfo }
    promocode: {},
    jackpots: {},
    casinoActions: { create: false, config: false, delete: false },
    info: {
      currency: {},
      casino: []
    }
  },
  reducers: {
    setUserId: (state, action) => {
      action.payload
        ? setLcs(ENUM_LOCALSTORAGE.userId, action.payload)
        : localStorage.removeItem(ENUM_LOCALSTORAGE.userId);
      state.id = action.payload;
    },
    setLogin: (state, action) => {
      action.payload
        ? setLcs(ENUM_LOCALSTORAGE.login, action.payload)
        : localStorage.removeItem(ENUM_LOCALSTORAGE.login);

      state.login = action.payload;
    },
    setUserType: (state, action) => {
      action.payload
        ? setLcs(ENUM_LOCALSTORAGE.type, action.payload)
        : localStorage.removeItem(ENUM_LOCALSTORAGE.type);
      state.type = action.payload;
    },
    setSuperUser: (state, action) => {
      action.payload
        ? setLcs(ENUM_LOCALSTORAGE.superUser, action.payload)
        : localStorage.removeItem(ENUM_LOCALSTORAGE.superUser);
      state.superUser = action.payload;
    },
    setMainGroup: (state, action) => {
      state.group.main = action.payload;
      if (action.payload) fillGroupHash(state, [action.payload]);
    },
    setChildrenGroup: (state, action) => {
      const children = action.payload;
      const newChildren = {};

      for (let childId in children) {
        const child = [...children[childId]];
        newChildren[childId] = [];
        child.forEach((el) => {
          newChildren[childId].push({ ...el, parentId: childId });
        });
      }

      state.group.children = newChildren;
      fillGroupHash(state, Object.values(newChildren).flat());
    },
    setGroupData: (state, action) => {
      const { id, data } = action.payload;
      data.groupId = +id;
      addGroupHash(state, id, { ...data });
      updateChildrenItem(state, id, data.parentId, data);
    },

    setGroupUpdate: (state, action) => {
      const { id, data } = action.payload;
      data.groupId = +id;
      const newData = { ...state.group.hash[id], ...data };
      addGroupHash(state, id, newData);
      updateChildrenItem(state, id, data.parentId, data);
    },
    setGroupHash(state, action) {
      state.group.hash = action.payload;
    },
    setNewBonus: (state, action) => {
      const { id, data } = action.payload || {};
      const prevBonus = { ...state.bonus };
      prevBonus[id] = data;
      state.bonus = prevBonus;
    },
    setUpdateBonus: (state, action) => {
      const { id, bonusName, data } = action.payload || {};
      if (!state.bonus[id]) {
        state.bonus = { ...state.bonus, [id]: { [bonusName]: data } };
      }
      if (!state.bonus[id][bonusName]) {
        state.bonus[id] = { ...state.bonus[id], [bonusName]: data };
      }
      state.bonus[id][bonusName] = data;
    },
    setCurrencyList: (state, action) => {
      state.info.currency = action.payload;
    },
    setCasinoList: (state, action) => {
      state.info.casino = action.payload;
    },
    setCasinoActions: (state, action) => {
      state.casinoActions = action.payload;
    },
    setCasinoActionsConfig: (state, action) => {
      state.casinoActions.config = action.payload;
    },
    setPromocodeSeries: (state, action) => {
      const { id, data } = action.payload || {};
      const newPromocode = { ...state.promocode };

      if (!newPromocode[id]) {
        newPromocode[id] = { series: data };
      } else newPromocode[id].series = data;

      state.promocode = newPromocode;
    },
    setPromocodeList: (state, action) => {
      const { id, data } = action.payload || {};
      const newPromocode = { ...state.promocode };

      if (!newPromocode[id]) {
        newPromocode[id] = { list: data };
      } else newPromocode[id].list = data;

      state.promocode = newPromocode;
    },
    setJackpotsByGroupId: (state, action) => {
      const { id, data } = action.payload || {};
      const newJackpots = { ...state.jackpots };

      if (!newJackpots[id]) {
        newJackpots[id] = data;
      } else newJackpots[id] = data;

      state.jackpots = newJackpots;
    }
  }
});

export default userSlice.reducer;

export const {
  setUserId,
  setUserType,
  setSuperUser,
  setMainGroup,
  setChildrenGroup,
  setNewBonus,
  setCurrencyList,
  setCasinoList,
  setGroupData,
  setGroupUpdate,
  setLogin,
  setGroupHash,
  setUpdateBonus,
  setPromocodeList,
  setJackpotsByGroupId,
  setCasinoActions,
  setPromocodeSeries,
  setCasinoActionsConfig
} = userSlice.actions;
